import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useInView } from 'react-intersection-observer';
import { useAccount, useAlgoliaContent, useCart } from '@/frontastic';
import {
  SEASONAL_SAVINGS,
  RECENTLY_VIEWED,
  SIMILAR_ITEMS,
  ALSO_BOUGHT_CONTENT,
  RELATED_ITEMS,
} from '@/helpers/constants/algoliaRecommend';
import { lazyloadconfig } from '@/helpers/utils/brandButton';
import { mapLocaleToMeaningfulFormat } from '@/helpers/utils/i18n';
import { routerUrl } from '@/helpers/utils/routerUrl';
import AlsoBoughtContent from './alsoBoughtContent';
import CLPProductListCarouselSkeleton from './clpProductListCarouselSkeleton';
import ProductListCarouselSkeleton from './productListCarouselSkeleton';
import RecentlyViewed from './recentlyViewed';
import RelatedItemsContent from './relatedItemsContent';
import SeasonalSavings from './seasonalSavings';
import SimilarItems from './similarItems';

interface TextFieldGroupItem {
  textField: string;
}

interface ProductListCarouselProps {
  data?: any;
  title?: any;
  selectOption?: 'seasonalSavings' | 'recentlyViewed' | string;
  textFieldsGroup?: TextFieldGroupItem[];
  siteId?: string;
  maxRecentlyViewedProductsPerPage?: number;
  maxSeasonalSavingsProductsPerPage?: number;
  maxSimilarProductsPerPage?: number;
  maxAlsoBoughtContentPerPage?: number;
  maxRelatedProductsPerPage?: number;
}

export const ProductListCarousel: React.FC<ProductListCarouselProps> = ({
  data,
  title,
  selectOption,
  siteId,
  maxSimilarProductsPerPage,
  maxAlsoBoughtContentPerPage,
  maxRelatedProductsPerPage,
}) => {
  const { loggedIn } = useAccount();
  const { recentlyViewed } = useAlgoliaContent();
  const { isEmpty } = useCart();
  const pathname = usePathname();
  const [isLoading, setIsLoading] = useState(true);
  const [isProductsHave, setProductsHave] = useState(false);
  const [recentlyViewedItems, setRecentlyViewedItems] = useState<any[]>([]);
  const [shouldRenderSection, setShouldRenderSection] = useState(true);
  const [currentPage, setCurrentPage] = useState('');
  const [isPDPpage, setIsPDPpage] = useState(false);

  useEffect(() => {
    const storedPage = sessionStorage.getItem('currentPage');
    setCurrentPage(storedPage ?? '');
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      await new Promise((resolve) => setTimeout(resolve, 2000));
      setIsLoading(false);
    };
    if (selectOption !== RECENTLY_VIEWED) {
      fetchData();
    }
    if (typeof window !== 'undefined') {
      const isPDPpage = window?.location?.href?.indexOf('/product/') > -1;
      setIsPDPpage(isPDPpage);
    }
  }, []);

  const { ref, inView } = useInView({
    rootMargin: lazyloadconfig.rootMargin,
    triggerOnce: true,
    skip: selectOption === SIMILAR_ITEMS && isPDPpage,
  });

  useEffect(() => {
    let productId = '';
    if (isPDPpage) {
      const urlParts = pathname.split('/');
      productId = urlParts[urlParts.length - 1];
    }
    const fetchRecentlyViewedItems = async () => {
      setIsLoading(true);
      const response: any = await recentlyViewed({ productId });
      if (!response.isError && response?.data?.results) {
        const filteredItems = response?.data?.results?.map((item: any) => item)?.filter((item: any) => item !== null);
        setRecentlyViewedItems(filteredItems);
        if (filteredItems?.length > 0) {
          setProductsHave(true);
        }
      } else {
        setRecentlyViewedItems([]);
      }
      setIsLoading(false);
    };
    if (selectOption === RECENTLY_VIEWED && inView) {
      fetchRecentlyViewedItems();
    }
  }, [loggedIn, inView]);

  const showRecentlyViewed = () => {
    const shouldRender =
      selectOption !== RECENTLY_VIEWED ||
      (selectOption === RECENTLY_VIEWED &&
        ((recentlyViewedItems && recentlyViewedItems.length > 0) || !inView) &&
        currentPage !== 'clp');
    setShouldRenderSection(shouldRender);
  };

  useEffect(() => {
    if (pathname === routerUrl?.cart) {
      if (!isEmpty) {
        showRecentlyViewed();
      } else {
        setShouldRenderSection(false);
      }
    } else {
      showRecentlyViewed();
    }
  }, [isEmpty, pathname, recentlyViewedItems]);

  return (
    <section ref={ref}>
      {shouldRenderSection && (
        <section
          className={`${
            pathname === routerUrl?.cart && isEmpty
              ? 'mx-auto max-w-screen-9xl'
              : 'relative mx-auto mt-5 max-w-screen-9xl px-4 pb-10 md:px-[60px] md:pb-10'
          }`}
        >
          {isProductsHave && (
            <section className="mb-5 flex items-center justify-between" id={selectOption}>
              <strong className="text-2xl font-bold leading-8 text-black">{title}</strong>
              {data?.data?.isShowViewAll && (
                <Link
                  href={data?.data?.viewAllLinkUrl || '#'}
                  className="text-base font-medium leading-[22.2px] text-black underline"
                  title={`Title: ${data?.data?.title}`}
                >
                  {mapLocaleToMeaningfulFormat().viewAll}
                </Link>
              )}
            </section>
          )}
          {isLoading || (!inView && selectOption !== SIMILAR_ITEMS && !isPDPpage) ? (
            <div
              id={selectOption === RECENTLY_VIEWED ? 'recentlyViewedContainer' : ''}
              className="skeleton-container mx-auto mt-10 max-w-screen-9xl pb-10 md:mt-20 md:pb-5"
            >
              <section className="skeleton-bg-none mb-5 flex animate-pulse items-center justify-between">
                <div className="bg-skeleton-bg-mode h-8 w-[180px] rounded-full bg-skeletonBg"></div>
                <div className="bg-skeleton-bg-mode h-5 w-[64px] rounded-full bg-skeletonBg"></div>
              </section>
              {currentPage === 'clp' ? <CLPProductListCarouselSkeleton /> : <ProductListCarouselSkeleton />}
            </div>
          ) : selectOption ? (
            selectOption === SEASONAL_SAVINGS ? (
              <SeasonalSavings siteId={siteId} setProductsHave={setProductsHave} />
            ) : selectOption === RECENTLY_VIEWED ? (
              <RecentlyViewed data={recentlyViewedItems || []} />
            ) : selectOption === RELATED_ITEMS ? (
              <RelatedItemsContent
                siteId={siteId}
                maxRelatedProductsPerPage={maxRelatedProductsPerPage}
                data={data}
                setProductsHave={setProductsHave}
              />
            ) : selectOption === SIMILAR_ITEMS ? (
              <SimilarItems
                siteId={siteId}
                maxSimilarProductsPerPage={maxSimilarProductsPerPage}
                data={data}
                setProductsHave={setProductsHave}
              />
            ) : selectOption === ALSO_BOUGHT_CONTENT ? (
              <AlsoBoughtContent
                siteId={siteId}
                maxAlsoBoughtContentPerPage={maxAlsoBoughtContentPerPage}
                data={data}
                setProductsHave={setProductsHave}
              />
            ) : (
              <></>
            )
          ) : (
            <></>
          )}
        </section>
      )}
    </section>
  );
};

export default ProductListCarousel;
